import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import {
  JiraAvailableProject,
  JiraProjectPermission,
  User,
} from "../../../../app/backendTypes";
import {
  useJiraAvailableProjects,
  usePendingJiraProjectAccessTask,
} from "../../../../app/hooks";
import Error from "../../Error";
import Loading from "../../Loading";
import UserSpan from "../../users/UserSpan";
import JiraAvailableProjectPicker from "./JiraAvailableProjectPicker";
import JiraProjectAccessDialog from "./JiraProjectAccessDialog";
import PendingAction from ".././PendingAction";
import Morselbar from "../../Morselbar";

function JiraProjectsDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
}) {
  const { open, setOpen, user } = props;

  const { projects, loading, error, reload } = useJiraAvailableProjects(
    user.employee.id,
    !open
  );

  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const [project, setProject] = useState<JiraAvailableProject | null>(null);

  useEffect(() => {
    if (project !== null) {
      const newProject = projects.find((p) => p.id === project.id);
      if (newProject !== undefined) {
        if (JSON.stringify(newProject) !== JSON.stringify(project))
          setProject(newProject);
      } else {
        setProject(null);
      }
    }
  }, [project, projects]);

  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogTitle>
          Jira Projects
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => {
              setProject(null);
              setOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Projects are derived based on the user and their manager's
            permissions
          </DialogContentText>
          <br />
          <JiraAvailableProjectPicker
            projects={projects}
            loading={loading}
            error={error}
            onChange={(val) => setProject(val)}
            initialValue={project}
          />
          <br />
          <br />
          {project !== null && (
            <ProjectPermissions
              user={user}
              project={project}
              callback={reload}
            />
          )}
          <br />
          <DialogContentText>
            For access in other projects visit the{" "}
            <Link
              target="_blank"
              href="https://jira.visma.com/servicedesk/customer/portal/8"
            >
              support portal
            </Link>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to load projects"
        error={error}
      />
    </>
  );
}

export default JiraProjectsDialog;

function ProjectPermissions(props: {
  user: User;
  project: JiraAvailableProject;
  callback: () => void;
}) {
  const { user, project, callback } = props;

  const { task, loading, error, reload } = usePendingJiraProjectAccessTask(
    user.employee.id,
    project.id
  );

  useEffect(() => {
    if (
      task !== null &&
      (task.status.kind === "Done" || task.status.kind === "Cancelled")
    )
      callback();
  }, [callback, task]);

  let element = <></>;
  const actions = [];

  if (loading) {
    actions.push(<Loading />);
  } else if (error) {
    actions.push(<Error error={error} />);
  } else if (task !== null) {
    actions.push(
      <PendingAction task={task} callback={reload} onCompleted={reload} />
    );
  }

  switch (project.cur_permission) {
    case JiraProjectPermission.None: {
      element = (
        <Typography>
          <UserSpan user={user} variant="body1" /> doesn't have{" "}
          <Typography component="span" color="error">
            read
          </Typography>{" "}
          or{" "}
          <Typography component="span" color="error">
            write
          </Typography>{" "}
          permissions
        </Typography>
      );

      if (actions.length === 0) {
        if (
          project.max_permission === JiraProjectPermission.Read ||
          project.max_permission === JiraProjectPermission.Write
        )
          actions.push(
            <RequestReadButton user={user} project={project} reload={reload} />
          );

        if (project.max_permission === JiraProjectPermission.Write)
          actions.push(
            <RequestWriteButton user={user} project={project} reload={reload} />
          );
      }

      break;
    }

    case JiraProjectPermission.Read: {
      element = (
        <Typography>
          <UserSpan user={user} variant="body1" /> has{" "}
          <Typography component="span" color="success.main">
            read
          </Typography>{" "}
          permissions
        </Typography>
      );

      if (actions.length === 0) {
        if (project.max_permission === JiraProjectPermission.Write)
          actions.push(
            <RequestWriteButton user={user} project={project} reload={reload} />
          );
      }

      break;
    }

    case JiraProjectPermission.Write: {
      element = (
        <Typography>
          <UserSpan user={user} variant="body1" /> has{" "}
          <Typography component="span" color="success.main">
            read
          </Typography>{" "}
          and{" "}
          <Typography component="span" color="success.main">
            write
          </Typography>{" "}
          permissions
        </Typography>
      );
      break;
    }
  }

  return (
    <>
      {element}
      <br />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {actions.map((a, i) => (
          <Fragment key={i}>{a}</Fragment>
        ))}
      </Box>
    </>
  );
}

function RequestReadButton(props: {
  user: User;
  project: JiraAvailableProject;
  reload: () => void;
}) {
  const { user, project, reload } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)} color="secondary">
        Get Read Only
      </Button>
      <JiraProjectAccessDialog
        open={open}
        setOpen={setOpen}
        user={user}
        project={project}
        permission={JiraProjectPermission.Read}
        callback={reload}
      />
    </>
  );
}

function RequestWriteButton(props: {
  user: User;
  project: JiraAvailableProject;
  reload: () => void;
}) {
  const { user, project, reload } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)} color="secondary">
        Get Read & Write
      </Button>
      <JiraProjectAccessDialog
        open={open}
        setOpen={setOpen}
        user={user}
        project={project}
        permission={JiraProjectPermission.Write}
        callback={reload}
      />
    </>
  );
}
